import { useCallback, useState } from 'react';
import { Appointment, Patient } from '@medplum/fhirtypes';
import { LeadStatus } from '@bariendo/lib/active-lead';
import { useMedplum } from '@medplum/react';
import { EMPLOYER_EXTENSION, INTERESTED_SERVICE_EXTENSION, MARKET_LOCATION_EXTENSION } from '@bariendo/lib/extensions';
import { collectsPayment, Track } from '@bariendo/lib/constants';
import { getStoredPaymentStatus, PaymentStatus, standingWonAt, WonHistoryEvent } from '@bariendo/lib/payment-status';
import { SwitchTrackModal } from '@/components/SwitchTrackModal/SwitchTrackModal';
import { alert } from '@/services/alert';
import { reportError } from '@/services/error-reporting';
import { fetchBot } from '@/services/services';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { PaymentOrderState } from '@/hooks/usePaymentOrder';
import { RefundHistoryEntry } from '@/services/Timeline/refundCards';
import { InsuranceCard } from '../Insurance/useInsuranceCard';
import { Section } from '../shared/Section';
import { BillingGroup } from './BillingGroup';
import { IntakeGroup } from './IntakeGroup';
import { moneyRows } from './services/billing';
import { panelLocks } from './services/locks';
import { useIntakeBilling } from './useIntakeBilling';
import { usePanelPaymentHistory } from './usePanelPaymentHistory';

interface IntakeBillingSectionProps {
  patient: Patient;
  /** From `appointmentsContext` — one Appointment query for the whole page. */
  appointments: Appointment[];
  /** The procedure-filtered slice of the same query, as the header pill uses. */
  procedures: Appointment[];
  /** From `paymentOrderContext` — one order read for the whole page. Absent for
   *  a viewer outside PAYMENTS_ROLES, which correctly drops the money rows. */
  paymentOrder?: PaymentOrderState;
  /** From `refundRequestsContext`: the page's one refund read, which the + Add menu and the refund
   *  composer share. Undefined until read, after a failed read, and outside the payments roles. */
  refundRequests?: RefundHistoryEntry[];
  /** `useInsuranceCard`, shared with the Insurance section below. */
  insurance: InsuranceCard;
  /** `patientContext.refreshPatient` — re-reads after the bot has written. */
  onPatientChanged: () => Promise<void>;
}

/**
 * The section the Patient Access team reads on every patient.
 *
 * It answers two questions a chronological timeline answers badly — *where is
 * this patient* and *what do they owe*. The order card is anchored at its
 * creation time, so it sinks down the feed as events append above it, which
 * makes the timeline a poor place to answer "does this patient still owe us
 * money?" — a question asked before every call.
 *
 * **Default-open**, unlike most sections here: one read on every patient
 * shouldn't start closed.
 */
export function IntakeBillingSection({
  patient,
  appointments,
  procedures,
  paymentOrder,
  insurance,
  refundRequests,
  onPatientChanged,
}: IntakeBillingSectionProps) {
  const medplum = useMedplum();
  const { refresh, ...data } = useIntakeBilling(patient);
  const { track, loading: trackLoading, reload: reloadTrack } = usePatientTrack(patient.id);
  // The row only opens the dialog; the destination is chosen inside it, where
  // the consequences of each one are stated before anything is written.
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const { currentUserPermissions } = useCurrentUser();
  const { canAccessPayments, canViewPayments } = currentUserPermissions;
  const now = new Date();

  // Stored on the Patient by the stage bot (BAR-3082), so every role reads Lead status and the
  // locks from it; the money behind it is for PAYMENTS_VIEW_ROLES only.
  const stored = getStoredPaymentStatus(patient);
  const locks = panelLocks(stored?.status, procedures, now);
  const history = usePanelPaymentHistory(
    patient,
    canViewPayments,
    stored ? `${stored.status}|${stored.at}` : undefined
  );
  const wonAt = wonDate(
    history.wonEvents,
    locks.cancelled,
    stored?.status === PaymentStatus.WON ? stored.at : undefined
  );
  const cancellation = locks.cancelled ? latestCancellation(refundRequests) : undefined;

  // Payment surfaces are hidden, not disabled, for anyone outside
  // PAYMENTS_VIEW_ROLES. `paymentOrder` is already undefined for them upstream;
  // this is the explicit second gate. Finance sees these rows but not the
  // Change-track action, which stays on canAccessPayments (BAR-2648).
  //
  // Nothing is drawn from the order while it or the track is loading: the page doesn't remount on a
  // patient switch, so both still hold the last patient's until the next reads land. No recorded
  // track is self-pay, as the stage bot reads it. And the order only counts on a track that charges
  // the patient: one left open across a switch to employer or insurance names no Package.
  const paysTrack = !trackLoading && collectsPayment(track ?? Track.D2C) ? (track ?? Track.D2C) : undefined;
  const orderState = canViewPayments && paymentOrder && !paymentOrder.loading && paysTrack ? paymentOrder : undefined;
  const order = orderState?.order ?? undefined;
  const money =
    orderState && paysTrack
      ? moneyRows(orderState.order, orderState.ledger, refundRequests ?? [], {
          track: paysTrack,
          cancelled: locks.cancelled,
          now,
        })
      : [];

  // Written through a bot, not `updatePatient`. `CareIntakeAccessPolicy` has
  // Patient readonly — PAC is deliberately least-privilege (BAR-1761) — so a
  // direct write 403s for exactly the role this panel is built for. The bot's
  // own membership policy grants Patient write, and the handler refuses any url
  // outside the intake-selection extensions (Package, Location, Employer).
  const setSelection = useCallback(
    async (url: string, value: string) => {
      if (!patient.id) return;

      try {
        await fetchBot(medplum, 'set-patient-intake-selection', { patientId: patient.id, url, value });
        await onPatientChanged();
      } catch (error) {
        // `fetchBot` throws, and an unhandled rejection here would leave the rep
        // believing the edit saved. Same treatment AboutSection gives its
        // writes. Rethrown so the row knows to stay open.
        reportError(error, { component: 'IntakeBillingSection' });
        alert.error("Couldn't save that change. Please try again.");
        throw error;
      }
    },
    [medplum, onPatientChanged, patient.id]
  );

  const handlePackageChange = useCallback(
    (value: string) => setSelection(INTERESTED_SERVICE_EXTENSION, value),
    [setSelection]
  );

  const handleEmployerChange = useCallback((value: string) => setSelection(EMPLOYER_EXTENSION, value), [setSelection]);

  const handleLocationChange = useCallback(
    (value: string) => setSelection(MARKET_LOCATION_EXTENSION, value),
    [setSelection]
  );

  const handleLeadStatusChange = useCallback(
    async (status: LeadStatus) => {
      if (!patient.id) return;

      try {
        await fetchBot(medplum, 'set-lead-status', { patientId: patient.id, status });
        await refresh();
      } catch (error) {
        // Same reasoning as the Package/Location write: `fetchBot` throws, and
        // an unhandled rejection would leave the toggle looking like it stuck.
        reportError(error, { component: 'IntakeBillingSection' });
        alert.error("Couldn't update the lead status. Please try again.");
      }
    },
    [medplum, patient.id, refresh]
  );

  return (
    <Section title="Intake and billing" defaultOpen>
      <IntakeGroup
        patient={patient}
        appointments={appointments}
        procedures={procedures}
        data={data}
        locks={locks}
        orderPackage={order ? packageName(order.items) : undefined}
        // Outside the payments roles the order is never read, so the panel can't tell whether one
        // has fixed the Package: they read it rather than edit a value an order may override.
        canEditPackage={canViewPayments}
        requestedAt={order?.createdAt}
        wonAt={wonAt}
        cancellation={cancellation}
        insurance={insurance}
        onPackageChange={handlePackageChange}
        onLocationChange={handleLocationChange}
        // The employer directory admits payments roles only; everyone else reads the row.
        onEmployerChange={canAccessPayments ? handleEmployerChange : undefined}
        onLeadStatusChange={handleLeadStatusChange}
        track={track}
        onChangeTrack={canAccessPayments ? () => setTrackModalOpen(true) : undefined}
        now={now}
      />
      <BillingGroup paymentStatus={canViewPayments ? stored : undefined} rows={money} />
      <SwitchTrackModal
        patient={patient}
        currentTrack={track}
        opened={trackModalOpen}
        onClose={() => setTrackModalOpen(false)}
        onSwitched={reloadTrack}
      />
    </Section>
  );
}

/** The first order line that came back hydrated. */
function packageName(items: { serviceName: string | null }[]): string | undefined {
  return items.find((item) => item.serviceName)?.serviceName ?? undefined;
}

/**
 * The Won the panel dates: the standing one, or, once a cancellation has reversed it, the latest,
 * which keeps its row beside Cancelled. A reversal with no cancellation (a refund outside a request,
 * a reopened order) takes the row away, as it takes the Won lock. Without the history (a role outside
 * the payments roles, or a failed read), the stored status's date while it is Won.
 */
function wonDate(
  events: WonHistoryEvent[] | undefined,
  cancelled: boolean,
  storedWonAt: string | undefined
): string | undefined {
  if (!events) return storedWonAt;
  if (!cancelled) return standingWonAt(events);

  return events
    .filter((event) => event.kind === 'WON')
    .map((event) => event.occurredAt)
    .sort()
    .pop();
}

/** The newest request that cancels the procedure: its date and reason. */
function latestCancellation(refunds: RefundHistoryEntry[] | undefined): { at: string; reason: string } | undefined {
  const latest = refunds
    ?.filter((request) => request.cancel)
    .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))[0];

  return latest ? { at: latest.requestedAt, reason: latest.reason } : undefined;
}
